import { useTranslation } from 'Components/Helpers/translate';
import { useAIChatDispatch } from '../Context/AIChatContext';
import { useSoftDeployAccess } from 'App/components/hooks';
import { Addon } from 'Store/actions/FeatureHelperActions';
import BemClass from '@upsales/components/Utils/bemClass';
import { doSend } from '../Context/Helpers';
import { Flex, Text } from '@upsales/components';
import React, { useState, useEffect } from 'react';
import Suggestion from '../Suggestion';

import './SuggestedQuestions.scss';

type Question = {
	text: string;
	icon: string;
};

type Tool = {
	id: string;
	icon: string;
	title: string;
	description: string;
	questions: Question[];
};

type SuggestedQuestionsProps = {
	onQuestionSelect?: () => void;
	resetKey?: number;
};

const SuggestedQuestions = ({ onQuestionSelect, resetKey = 0 }: SuggestedQuestionsProps = {}) => {
	const classes = new BemClass('SuggestedQuestions');
	const { t } = useTranslation();
	const dispatch = useAIChatDispatch();
	const [selectedTool, setSelectedTool] = useState<string | null>(null);

	useEffect(() => {
		setSelectedTool(null);
	}, [resetKey]);

	const hasAIChatCreate = useSoftDeployAccess('AI_CHAT_CREATE');
	const hasPreMeetingResearchFlag = useSoftDeployAccess('AI_PRE_MEETING_RESEARCH');
	const hasPreMeetingResearch =
		hasPreMeetingResearchFlag && Tools.FeatureHelper.isAvailableAddon(Addon.AI_PRE_MEETING_RESEARCH);

	const createQuestions: Question[] = [
		{
			text: t('ai.chat.suggestion.createTodo.reminder', 'Create a reminder to call John tomorrow'),
			icon: 'bell'
		},
		{
			text: t('ai.chat.suggestion.createTodo.followup', 'Create a task to follow up on proposal'),
			icon: 'check-circle'
		},
		{
			text: t('ai.chat.suggestion.createTodo.meeting', 'Remind me to prepare for meeting next week'),
			icon: 'calendar'
		},
		{
			text: t('ai.chat.suggestion.bookAppointment.appointment', 'Book an appointment for tomorrow'),
			icon: 'calendar'
		},
		{
			text: t('ai.chat.suggestion.bookPhonecall.phonecall', 'Book a phone call for tomorrow'),
			icon: 'phone'
		},
		...(hasPreMeetingResearch
			? [
				{
					text: t('ai.chat.suggestion.preMeetingResearch', 'Create Pre-Meeting Research'),
					icon: 'file-text'
				}
			]
			: [])
	];

	const mainTools: Tool[] = [
		...(hasAIChatCreate
			? [
				{
					id: 'create',
					icon: 'plus',
					title: t('ai.chat.category.create.title', 'Create'),
					description: t('ai.chat.category.create.description', 'Create new items'),
					questions: createQuestions
				}
			]
			: []),
		{
			id: 'search',
			icon: 'search',
			title: t('ai.chat.tool.search.title', 'Search CRM'),
			description: t('ai.chat.tool.search.description', 'Find clients and orders'),
			questions: [
				{
					text: t('ai.chat.suggestion.search.orders', 'Show my recent orders'),
					icon: 'dollar'
				},
				{
					text: t('ai.chat.suggestion.search.pipeline', 'List pipeline opportunities'),
					icon: 'opportunity'
				},
				{
					text: t('ai.chat.suggestion.search.myClients', 'Show my active clients'),
					icon: 'building-o'
				}
			]
		},
		{
			id: 'calculate',
			icon: 'calculator',
			title: t('ai.chat.tool.calculate.title', 'Calculate'),
			description: t('ai.chat.tool.calculate.description', 'Calculate totals and statistics'),
			questions: [
				{
					text: t('ai.chat.suggestion.calculate.totalOrders', 'What is the total value of my orders?'),
					icon: 'calculator'
				},
				{
					text: t('ai.chat.suggestion.calculate.averageDeal', 'What is the average deal size?'),
					icon: 'calculator'
				},
				{
					text: t('ai.chat.suggestion.calculate.totalRevenue', 'How much revenue do I have this year?'),
					icon: 'calculator'
				},
				{
					text: t('ai.chat.suggestion.calculate.budget', 'How much budget do I have for 2025?'),
					icon: 'calculator'
				}
			]
		},
		{
			id: 'statusReport',
			icon: 'bar-chart',
			title: t('ai.chat.tool.statusReport.title', 'Status Report'),
			description: t('ai.chat.tool.statusReport.description', 'View quota progress and performance'),
			questions: [
				{
					text: t('ai.chat.suggestion.statusReport.me', 'How am I doing this year?'),
					icon: 'chart-line'
				},
				{
					text: t('ai.chat.suggestion.statusReport.team', 'How is the role ... doing?'),
					icon: 'chart-line'
				},
				{
					text: t('ai.chat.suggestion.statusReport.performance', 'How am I performing this quarter?'),
					icon: 'chart-line'
				}
			]
		},
		{
			id: 'recommendOpportunityAction',
			icon: 'opportunity',
			title: t('ai.chat.tool.recommendOpportunityAction.title', 'Recommend Actions'),
			description: t(
				'ai.chat.tool.recommendOpportunityAction.description',
				'Get recommendations for opportunities'
			),
			questions: [
				{
					text: t(
						'ai.chat.suggestion.recommendOpportunityAction.whatNext',
						'What should I do with this opportunity?'
					),
					icon: 'opportunity'
				},
				{
					text: t(
						'ai.chat.suggestion.recommendOpportunityAction.nextSteps',
						'What are the next steps for this deal?'
					),
					icon: 'opportunity'
				},
				{
					text: t(
						'ai.chat.suggestion.recommendOpportunityAction.recommend',
						'Recommend actions for opportunity'
					),
					icon: 'opportunity'
				}
			]
		},
		{
			id: 'knowledgebase',
			icon: 'question-circle',
			title: t('ai.chat.tool.knowledgebase.title', 'Get Help'),
			description: t('ai.chat.tool.knowledgebase.description', 'Find how-to guides and tutorials'),
			questions: [
				{
					text: t('ai.chat.suggestion.knowledgebase.tasks', 'How do I use tasks?'),
					icon: 'check-circle'
				},
				{
					text: t('ai.chat.suggestion.knowledgebase.easybooking', 'How does Easy Booking work?'),
					icon: 'calendar'
				},
				{
					text: t('ai.chat.suggestion.knowledgebase.portfolio', 'How does Customer Portfolio work?'),
					icon: 'briefcase'
				}
			]
		}
	];

	const secondaryTools: Tool[] = [
		{
			id: 'feedback',
			icon: 'comment',
			title: t('ai.chat.tool.feedback.title', 'Give Feedback'),
			description: t('ai.chat.tool.feedback.description', 'Share your thoughts'),
			questions: [
				{
					text: t('ai.chat.suggestion.feedback.positive', 'This is working great!'),
					icon: 'thumbs-up'
				},
				{
					text: t('ai.chat.suggestion.feedback.improvement', 'I have suggestions for improvement'),
					icon: 'comment'
				},
				{
					text: t('ai.chat.suggestion.feedback.bug', 'I found an issue'),
					icon: 'exclamation-triangle'
				}
			]
		},
		{
			id: 'feature',
			icon: 'lightbulb',
			title: t('ai.chat.tool.feature.title', 'Request Feature'),
			description: t('ai.chat.tool.feature.description', 'Suggest new functionality'),
			questions: [
				{
					text: t('ai.chat.suggestion.feature.experience', 'Can you improve the user experience?'),
					icon: 'star'
				},
				{
					text: t('ai.chat.suggestion.feature.feature', 'Can you add a feature that...'),
					icon: 'lightbulb'
				},
				{
					text: t('ai.chat.suggestion.feature.automation', 'Can you add automation for...?'),
					icon: 'cog'
				}
			]
		},
		{
			id: 'tonality',
			icon: 'user',
			title: t('ai.chat.tool.tonality.title', 'Change Style'),
			description: t('ai.chat.tool.tonality.description', 'Adjust communication tone'),
			questions: [
				{
					text: t('ai.chat.suggestion.tonality.professional', 'Please speak more professionally'),
					icon: 'briefcase'
				},
				{
					text: t('ai.chat.suggestion.tonality.casual', 'Be more casual and friendly'),
					icon: 'smile'
				},
				{
					text: t('ai.chat.suggestion.tonality.technical', 'Use more technical language'),
					icon: 'code'
				},
				{
					text: t('ai.chat.suggestion.tonality.executive', "Speak like you're talking to an executive"),
					icon: 'user-tie'
				}
			]
		}
	];

	const handleToolSelect = (toolId: string) => {
		setSelectedTool(toolId);
	};

	const handleQuestionSelect = (question: string) => {
		dispatch(doSend({ message: question }));
		onQuestionSelect?.();
	};

	const handleBackToCategories = () => {
		setSelectedTool(null);
	};

	const findTool = (toolId: string): Tool | undefined => {
		return [...mainTools, ...secondaryTools].find(t => t.id === toolId);
	};

	if (selectedTool) {
		const tool = findTool(selectedTool);
		if (!tool) return null;

		return (
			<Flex direction="column" gap="u2" className={classes.b()}>
				<Flex justifyContent="flex-start" alignItems="center" gap="u2" space="pbs">
					<Suggestion
						icon="arrow-left"
						title={t('default.back', 'Back')}
						onClick={handleBackToCategories}
						fullWidth={false}
					/>
				</Flex>
				<Flex direction="column" alignItems="center" gap="u2">
					{tool.questions.map((question, index) => (
						<Suggestion
							key={index}
							icon={question.icon as any}
							title={question.text}
							onClick={() => handleQuestionSelect(question.text)}
						/>
					))}
				</Flex>
			</Flex>
		);
	}

	return (
		<Flex direction="column" className={classes.b()}>
			<Flex direction="column" alignItems="center" gap="u2" className={classes.elem('main').b()}>
				{mainTools.map(tool => (
					<Suggestion
						key={tool.id}
						icon={tool.icon as any}
						title={tool.title}
						onClick={() => handleToolSelect(tool.id)}
					/>
				))}
			</Flex>
			<Flex direction="column" alignItems="center" gap="u2" className={classes.elem('secondary').b()}>
				<Text size="sm" color="grey-11" className={classes.elem('section-title').b()}>
					{t('ai.chat.suggestedQuestions.other', 'Övrigt')}
				</Text>
				{secondaryTools.map(tool => (
					<Suggestion
						key={tool.id}
						icon={tool.icon as any}
						title={tool.title}
						onClick={() => handleToolSelect(tool.id)}
					/>
				))}
			</Flex>
		</Flex>
	);
};

export default SuggestedQuestions;
